import type { AdminOrderListItemDto } from '@carcheck/shared';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { GridColDef } from '@mui/x-data-grid';
import type { TFunction } from 'i18next';
import type { ReactElement } from 'react';

import { initials, shortId } from './displayName';
import { RedactedField } from '../../../components/pii';
import { tokens } from '../../../design';
import { formatCurrency, formatDate } from '../../../utils/format';
import { OrderStatusBadge } from '../_components/OrderStatusBadge';

/**
 * Column definitions for the orders DataGrid (AUT-123), extracted from the
 * page so the page component stays under the line cap. Sorting is disabled
 * per column — the backend list is fixed `createdAt DESC` (no arbitrary
 * sort), so the grid never sends a sort the server can't honour.
 *
 * Identity cells (customer / inspector name) render through
 * `<RedactedField perm="view_pii">`, which agrees with `row.piiRevealed`
 * (both derive from the same backend permission). A revealed value is
 * never re-redacted; only revealed values get a copy button. Null names
 * fall back to the no-value dash (mirrors the users email cell). The
 * inspector cell additionally handles `inspectorId === null` (unassigned).
 */

/** Initials when a name is present, else the unassigned/none dash. */
const PartyAvatar = ({ name }: { name: string | null }): ReactElement => (
  <Avatar sx={{ width: 30, height: 30, fontSize: 11, fontWeight: 700 }}>{initials(name)}</Avatar>
);

const NameValue = ({
  name,
  revealed,
  t,
}: {
  name: string | null;
  revealed: boolean;
  t: TFunction;
}): ReactElement => {
  if (!name) {
    return (
      <Typography variant="body2" sx={{ color: tokens.text.tertiary }} noWrap>
        {t('orders.detail.noValue')}
      </Typography>
    );
  }
  return (
    <Typography variant="body2" fontWeight={600} noWrap component="span">
      <RedactedField
        perm="view_pii"
        value={name}
        redacted={name}
        {...(revealed ? { copyable: true } : {})}
      />
    </Typography>
  );
};

const PartyCell = ({
  name,
  revealed,
  t,
}: {
  name: string | null;
  revealed: boolean;
  t: TFunction;
}): ReactElement => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, height: '100%', minWidth: 0 }}>
    <PartyAvatar name={name} />
    <NameValue name={name} revealed={revealed} t={t} />
  </Box>
);

/** Inspector cell — renders the unassigned placeholder when `inspectorId`
 * is null, else the PII-gated name (mirrors `PartyCell`). */
const InspectorCell = ({ row, t }: { row: AdminOrderListItemDto; t: TFunction }): ReactElement =>
  row.inspectorId === null ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, height: '100%' }}>
      <PartyAvatar name={null} />
      <Typography variant="body2" sx={{ color: tokens.text.tertiary }} noWrap>
        {t('orders.unassigned')}
      </Typography>
    </Box>
  ) : (
    <PartyCell name={row.inspectorName} revealed={row.piiRevealed} t={t} />
  );

/** Order-number + short-id cell. */
const OrderCell = ({ row }: { row: AdminOrderListItemDto }): ReactElement => (
  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
    <Typography variant="body2" fontWeight={600} fontFamily="monospace">
      {`№ ${row.orderNumber}`}
    </Typography>
    <Typography variant="caption" sx={{ color: tokens.text.tertiary }} fontFamily="monospace">
      {shortId(row.id)}
    </Typography>
  </Box>
);

export const buildOrderColumns = (t: TFunction): GridColDef<AdminOrderListItemDto>[] => [
  {
    field: 'order',
    headerName: t('orders.columns.order'),
    flex: 1,
    minWidth: 160,
    sortable: false,
    renderCell: ({ row }) => <OrderCell row={row} />,
  },
  {
    field: 'customer',
    headerName: t('orders.columns.customer'),
    flex: 1.2,
    minWidth: 180,
    sortable: false,
    renderCell: ({ row }) => <PartyCell name={row.customerName} revealed={row.piiRevealed} t={t} />,
  },
  {
    field: 'inspector',
    headerName: t('orders.columns.inspector'),
    flex: 1.2,
    minWidth: 180,
    sortable: false,
    renderCell: ({ row }) => <InspectorCell row={row} t={t} />,
  },
  {
    field: 'status',
    headerName: t('orders.columns.status'),
    flex: 0.8,
    minWidth: 140,
    sortable: false,
    renderCell: ({ row }) => <OrderStatusBadge status={row.status} />,
  },
  {
    field: 'total',
    headerName: t('orders.columns.total'),
    flex: 0.6,
    minWidth: 120,
    sortable: false,
    align: 'right',
    headerAlign: 'right',
    renderCell: ({ row }) => (
      <Typography variant="body2" fontFamily="monospace">
        {formatCurrency(Number(row.total), row.currency)}
      </Typography>
    ),
  },
  {
    field: 'createdAt',
    headerName: t('orders.columns.created'),
    flex: 0.7,
    minWidth: 120,
    sortable: false,
    renderCell: ({ row }) => (
      <Typography variant="body2" color="text.secondary" fontFamily="monospace">
        {formatDate(row.createdAt)}
      </Typography>
    ),
  },
];
